/// <reference types="@netfront/ekardo-content-library/types/window" />
import '@netfront/ui-library/dist/esm/css/index.css';
import '@netfront/ui-library/dist/css/global.css';
import '@netfront/ui-library/dist/css/helpers.css';
import '@netfront/ekardo-content-library/dist/esm/css/index.css';

import 'react-circular-progressbar/dist/styles.css';
import '../styles/components/index.css';
import '../styles/content-library/index.css';
import '../styles/globals.css';
import '../styles/ui-library/index.css';
import '../styles/utils.css';
import '../styles/cms-custom.css';
import '../styles/pages/index.css';
import 'animate.css';

import { AppProps } from 'next/app';
import isNil from 'lodash.isnil';
import omitBy from 'lodash.omitby';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { ApolloClientsProvider } from '@netfront/apollo-providers';
import { useGoogleAnalytics } from '@netfront/ekardo-content-library';

import { useUser } from '@netfront/gelada-identity-library';
import Head from 'next/head';
import LogRocket from 'logrocket';
import Providers from '@/components/Providers/Providers';
import { NextStep } from 'nextstepjs';
import { SpotlightCard } from '@/components/Spotlight/SpotlightCard';
import { getSpotLightTour } from '@/components/Spotlight/spotLightSteps';
import { useMediaQuery } from '@/hooks/useMediaQuery/useMediaQuery';
import { Toaster } from '@netfront/ui-library';

const SpotlightWrapper = ({ children }: { children: React.ReactNode }) => {
  const isLargeScreen = useMediaQuery({ customQuery: '(min-width: 1280px)' });
  const steps = useMemo(() => getSpotLightTour(isLargeScreen), [isLargeScreen]);

  return (
    <NextStep steps={steps} cardComponent={SpotlightCard}>
      {children}
    </NextStep>
  );
};

function AppInner({ Component, pageProps }: Pick<AppProps, 'Component' | 'pageProps'>) {
  const googleAnalyticsMeasurementId = String(process.env.REACT_APP_GOOGLE_ANALYTICS);
  const isGoogleAnalyticsDebugMode = String(process.env.REACT_APP_GOOGLE_ANALYTICS_DEBUG_MODE).toUpperCase() === 'Y';
  const [gtagConfigParams, setGtagConfigParams] = useState<ReturnType<typeof omitBy>>();

  const { trackPageView, canRunAuthenticated } = useGoogleAnalytics();
  const { events } = useRouter();
  const { getUser } = useUser();

  const user = getUser();

  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    LogRocket.init('sfzpuh/dawn-program');
    if (user) {
      LogRocket.identify(String(user.id), {
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
        email: user.credential?.email ?? '',
      });
    }
  }

  useEffect(() => {
    if (canRunAuthenticated || !user) {
      trackPageView(window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunAuthenticated]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (Boolean(googleAnalyticsMeasurementId) && (canRunAuthenticated || !user)) {
        trackPageView(window.location.pathname);
      }
    };

    events.on('routeChangeComplete', handleRouteChange);
    return () => events.off('routeChangeComplete', handleRouteChange);
  }, []);

  useEffect(() => {
    if (gtagConfigParams) {
      return;
    }

    setGtagConfigParams(() =>
      omitBy(
        {
          debug_mode: isGoogleAnalyticsDebugMode ? true : undefined,
          send_page_view: false,
          user_id: String(user?.id ?? ''),
          userId: String(user?.id ?? ''),
          app_user_id: String(user?.id ?? ''),
        },
        isNil,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtagConfigParams, user]);
  useEffect(() => {
    if (gtagConfigParams && typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', googleAnalyticsMeasurementId, gtagConfigParams);
    }
  }, [gtagConfigParams, googleAnalyticsMeasurementId]);

  return (
    <>
      <Head>
        {/* Global Site Tag (gtag.js) - Google Analytics */}
        <script src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}`} async />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            `,
          }}
        />
      </Head>
      <Providers>
        <SpotlightWrapper>
          <Toaster
            toastOptions={{
              duration: 2500,
            }}
          />

          <Component {...pageProps} />
        </SpotlightWrapper>
      </Providers>
    </>
  );
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const { push } = router;

  const onUnauthenticated = () => {
    if (typeof window === 'undefined') {
      return;
    }
    void push('/login');
  };

  return (
    <>
      <div id="root">
        <ApolloClientsProvider
          options={{
            projectId: process.env.REACT_APP_PROJECT_ID ?? '',
            onUnauthenticated,
            endpointSource: {
              source: 'environment variables',
            },
          }}
        >
          <AppInner Component={Component} pageProps={pageProps} />
        </ApolloClientsProvider>
      </div>
    </>
  );
}

// eslint-disable-next-line import/no-default-export
export default App;
